import React from 'react';
import styled from 'styled-components';
import { Box, Grid } from '@mui/material';
import { type FormikErrors, useFormik } from 'formik';
import { object, array, string, boolean } from 'yup';
import dayjs from 'dayjs';
import { CHECK_TYPE, CONDITION_TYPE } from './constant';
import { conditionCheckOptions, conditionCheckTypeOptions } from './options';
import { PAGE_TEXT } from '../../../../lang';
import {
  type ConditionMeet,
  type Condition,
  type Conditions,
} from '../../../../graphql';
import { AssessmentAccordion } from '../../../../components';
import { getAcademicMap } from '../../../../../AcademicRegistry/components/ARQualificationCheck/AcademicQualificationCheck/getAcademicMap';
import { getLanguageQualificationMap } from '../../../../../AcademicRegistry/components/ARQualificationCheck/LanguageQualificationCheck/getLanguageQualificationMap';
import { getOtherTestMap } from '../../../../../AcademicRegistry/components/ARQualificationCheck/OtherTestQualificationCheck';
import { getOptionsLabel } from '../../../../../Monitor/components';
import {
  Accordion,
  Button,
  Select,
  TextField,
  InformationDisplayBlock,
  DatePicker,
  PhoneInputField,
} from '../../../../../../components';
import { shouldAdminUpdate } from '../../../../../Application/components/ApplicationStatus/Offer/ConditionMeet/meetConditionFunctions';

const defaultValue = 'Will Update';
const noValueDisplay = 'N/A';

type ConditionCheckFormProps = {
  conditionMeet: ConditionMeet;
  registrationDeadline: string;
  viewMode: boolean;
  submitConditionMeet: (values: Conditions) => Promise<void>;
};

const CONDITION_CHECK = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.CONDITION_CHECK;
const CONTACT_PERSON = CONDITION_CHECK.CONTACT_PERSON;

export const ConditionalCheckForm: React.FC<ConditionCheckFormProps> = (
  props: ConditionCheckFormProps,
) => {
  const {
    conditionMeet,
    registrationDeadline,
    viewMode = false,
    submitConditionMeet,
  } = props;

  const {
    conditionFulfillingDeadline,
    contactDepartmentPersonName,
    contactDepartmentPersonEmail,
    contactDepartmentPersonPhone,
    conditions = [],
    academicQualifications = [],
    languageQualifications = [],
    otherTests = [],
    status,
  } = conditionMeet;

  const adminUpdate = shouldAdminUpdate(status);

  const formik = useFormik<Conditions>({
    initialValues: {
      contactPerson: {
        personName: contactDepartmentPersonName ?? '',
        personEmail: contactDepartmentPersonEmail ?? '',
        personPhone: contactDepartmentPersonPhone ?? '',
        deadline: conditionFulfillingDeadline ?? null,
      },
      conditions,
    },
    validateOnChange: false, // to prevent laggy-input problem [RPG-1101]
    validationSchema: object({
      contactPerson: object({
        personName: string(),
        personEmail: string().email('Invalid email'),
        personPhone: string().required('Required'),
        deadline: string().required('Required'),
      }),
      conditions: array().of(
        object({
          passed: boolean().required('Required'),
          remark: string().nullable(),
        }),
      ),
    }),
    onSubmit: submitConditionMeet,
  });

  const conditionsInfoDisplay: Partial<
    Record<CONDITION_TYPE, React.ReactNode>
  > = {
    [CONDITION_TYPE.GENERAL_REQUIREMENT]: (
      <>
        {(academicQualifications?.length ?? 0)
          ? academicQualifications?.map((item, index) => {
              const academicMap = getAcademicMap(item);
              const informationDisplayProps = {
                blockData: academicMap,
                defaultValue,
              };

              return (
                <AssessmentAccordion
                  key={index}
                  title={'Academic Qualification - ' + (index + 1)}
                  defaultExpanded
                  AssessDetails={
                    <Box
                      padding={3}
                      fontSize={16}
                      sx={{
                        backgroundColor: '#eeeeee',
                        marginBottom: '1rem',
                      }}
                    >
                      <InformationDisplayBlock {...informationDisplayProps} />
                    </Box>
                  }
                />
              );
            })
          : noValueDisplay}
      </>
    ),
    [CONDITION_TYPE.ENGLISH_REQUIREMENT]: (
      <>
        {(languageQualifications?.length ?? 0) ? (
          <Box
            padding={3}
            fontSize={16}
            sx={{
              backgroundColor: '#eeeeee',
              marginBottom: '1rem',
            }}
          >
            <InformationDisplayBlock
              {...{
                blockData: getLanguageQualificationMap(
                  languageQualifications[0],
                ),
                defaultValue,
              }}
            />
          </Box>
        ) : (
          noValueDisplay
        )}
      </>
    ),
    [CONDITION_TYPE.OTHER_TEST_REQUIREMENT]: (
      <>
        {(otherTests?.length ?? 0) ? (
          <Box
            padding={3}
            fontSize={16}
            sx={{
              backgroundColor: '#eeeeee',
              marginBottom: '1rem',
            }}
          >
            <InformationDisplayBlock
              {...{
                blockData: getOtherTestMap(otherTests[0]),
                defaultValue,
              }}
            />
          </Box>
        ) : (
          noValueDisplay
        )}
      </>
    ),
  };

  const handleClearError = async (name: string): Promise<void> => {
    function objectToPath(
      obj: Record<string, any>,
      parentKey = '',
      result: string[] = [],
    ): string[] {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const newKey = parentKey ? `${parentKey}.${key}` : key;
          if (typeof obj[key] === 'object' && obj[key] !== null) {
            objectToPath(obj[key], newKey, result);
          } else {
            result.push(newKey);
          }
        }
      }
      return result;
    }

    const errors = await formik.validateForm();
    const objPaths = objectToPath(errors);

    if (objPaths.includes(name)) {
      await formik.setFieldTouched(name, true);
    }
  };

  const conditionsErrors = formik.errors.conditions as Array<
    FormikErrors<Condition>
  >; // must add type protection

  const handleChangePassed =
    (index: number) =>
    async (
      e: React.ChangeEvent<{ name?: string; value: string }>,
    ): Promise<void> => {
      const { value } = e.target;
      const passed: boolean = value === CHECK_TYPE.MEET;
      await formik.setFieldValue(`conditions.${index}.passed`, passed);
      await handleClearError(`conditions.${index}.passed`);
    };

  return (
    <Accordion
      className="w-full"
      defaultExpanded
      title={CONDITION_CHECK.TITLE}
      details={
        <ContentBox>
          <AssessmentAccordion
            defaultExpanded
            title={CONTACT_PERSON.TITLE}
            AssessDetails={
              <div className="px-4 py-8">
                <div className="grid grid-cols-2 px-8 justify-items-start items-start gap-6">
                  <TextField
                    label={CONTACT_PERSON.NAME}
                    disabled={viewMode}
                    className="w-full"
                    name="contactPerson.personName"
                    value={formik.values.contactPerson.personName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.contactPerson?.personName &&
                      Boolean(formik.errors.contactPerson?.personName)
                    }
                    helperText={
                      formik.touched.contactPerson?.personName &&
                      formik.errors.contactPerson?.personName
                    }
                  />

                  <PhoneInputField
                    disabled={viewMode}
                    className="w-full"
                    label={CONTACT_PERSON.PHONE}
                    disableDropdown
                    name="contactPerson.personPhone"
                    value={
                      formik.values.contactPerson.personPhone.startsWith('852')
                        ? formik.values.contactPerson.personPhone
                        : '852'
                    }
                    onChange={async value => {
                      const phone = value.replace(/[^0-9]/g, '');
                      const phoneNumber = phone.startsWith('852')
                        ? phone.slice(0, 11)
                        : '852';
                      await formik.setFieldValue(
                        'contactPerson.personPhone',
                        phoneNumber,
                      );
                    }}
                    error={
                      formik.touched.contactPerson?.personPhone &&
                      Boolean(formik.errors.contactPerson?.personPhone)
                    }
                    helperText={
                      formik.touched.contactPerson?.personPhone &&
                      formik.errors.contactPerson?.personPhone
                    }
                  />
                  <TextField
                    label={CONTACT_PERSON.EMAIL}
                    disabled={viewMode}
                    className="w-full"
                    name="contactPerson.personEmail"
                    value={formik.values.contactPerson.personEmail}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.contactPerson?.personEmail &&
                      Boolean(formik.errors.contactPerson?.personEmail)
                    }
                    helperText={
                      formik.touched.contactPerson?.personEmail &&
                      formik.errors.contactPerson?.personEmail
                    }
                    onBlur={async () => {
                      await handleClearError('contactPerson.personEmail');
                    }}
                  />
                  <DatePicker
                    label={CONTACT_PERSON.CONDITION_DEADLINE}
                    disabled={viewMode}
                    className="w-full"
                    name="contactPerson.deadline"
                    minDate={dayjs()}
                    maxDate={dayjs(registrationDeadline)}
                    value={
                      formik.values.contactPerson.deadline !== null
                        ? dayjs(formik.values.contactPerson.deadline)
                        : null
                    }
                    onChange={async (date: any) => {
                      await formik.setFieldValue(
                        'contactPerson.deadline',
                        date,
                      );
                      await handleClearError('contactPerson.deadline');
                    }}
                    error={
                      formik.touched.contactPerson?.deadline &&
                      Boolean(formik.errors.contactPerson?.deadline)
                    }
                    helperText={
                      formik.touched.contactPerson?.deadline &&
                      formik.errors.contactPerson?.deadline
                    }
                  />
                </div>
              </div>
            }
          />
          <form onSubmit={formik.handleSubmit}>
            {formik.values.conditions?.map((condition: Condition, index) => {
              const { item, passed, remark } = condition;
              const passedValue =
                typeof passed !== 'boolean'
                  ? ''
                  : passed
                    ? CHECK_TYPE.MEET
                    : CHECK_TYPE.NOT_MEET;

              return (
                <AssessmentAccordion
                  defaultExpanded
                  key={index}
                  title={getOptionsLabel(conditionCheckTypeOptions, item)}
                  AssessDetails={
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        {conditionsInfoDisplay[item as CONDITION_TYPE]}
                      </Grid>
                      <Grid size={6}>{CONDITION_CHECK.CONDITION_CHECK}</Grid>
                      <Grid size={6}>
                        <Select
                          disabled={viewMode || !adminUpdate}
                          fullWidth
                          name={`conditions.${index}.passed`}
                          options={conditionCheckOptions}
                          value={passedValue}
                          onChange={handleChangePassed(index)}
                          error={
                            formik.touched.conditions?.[index]?.passed &&
                            Boolean(conditionsErrors?.[index]?.passed)
                          }
                          helperText={
                            formik.touched.conditions?.[index]?.passed &&
                            conditionsErrors?.[index]?.passed
                          }
                        />
                      </Grid>
                      <Grid size={12}>{CONDITION_CHECK.REMARKS}</Grid>
                      <Grid size={12}>
                        <TextField
                          fullWidth
                          disabled={viewMode || !adminUpdate}
                          name={`conditions.${index}.remark`}
                          value={remark}
                          onChange={formik.handleChange}
                          minRows={4}
                          multiline
                        />
                      </Grid>
                    </Grid>
                  }
                />
              );
            })}

            <div className={viewMode ? 'hidden' : 'buttons-area'}>
              <Button variant="contained" type="submit">
                {PAGE_TEXT.SAVE}
              </Button>
            </div>
          </form>
        </ContentBox>
      }
    />
  );
};

const ContentBox = styled.div`
  padding: 2rem 1rem;
  flex: 1;

  Accordion {
    margin-bottom: 1rem;
  }

  .MuiGrid-container {
    margin: 0 0 2rem 0;
    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0rem 1rem;
  }

  .MuiAccordionDetails-root {
    padding: 0;
  }

  .MuiButtonBase-root {
    min-height: 0px !important;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    button {
      margin: 0 1rem;
    }
  }
`;
